import { IDENTITY_TYPE } from '../../pages/Signup/components';
import { type StepperFormContentValues } from '../../pages/Signup/components/types';

export enum VIEW_MODE {
  // for applicant
  SIGN_UP = 'SIGN_UP',

  // for applicant after signup
  VIEW_AS_APPLICANT = 'VIEW_AS_APPLICANT',

  // for GS member
  VIEW_AS_GS_MEMBER = 'VIEW_AS_GS_MEMBER',

  // for AR member in "View as applicant" page
  VIEW_AS_AR_MEMBER = 'VIEW_AS_AR_MEMBER',

  // for GS member only, but prior to VIEW_AS_GS_MEMBER
  SUPER_ADMIN = 'SUPER_ADMIN',

  // in case there is a usage of "other" in the future
  OTHER = 'OTHER',
}

const COMMON_CONFIG = {
  addressCountryRegion: {
    isPresent: true,
    disabled: false,
  },
  addressLine1: {
    isPresent: true,
    disabled: false,
  },
  addressLine2: {
    isPresent: true,
    disabled: false,
  },
  addressLine3: {
    isPresent: true,
    disabled: false,
  },
  mobileNumber: {
    isPresent: true,
    disabled: false,
  },
  telephoneNumber: {
    isPresent: true,
    disabled: false,
  },
  disabilitySpecialType: {
    isPresent: true,
    disabled: false,
  },
  disabilitySpecialDetail: {
    isPresent: true,
    disabled: false,
  },
};

// Rule: APPLICANT_CONFIG must be a non-nested mapping object
const APPLICANT_CONFIG: Record<
  VIEW_MODE,
  Partial<Record<keyof StepperFormContentValues, any>>
> = {
  [VIEW_MODE.SIGN_UP]: {
    ...COMMON_CONFIG,
    email: {
      isPresent: false,
      disabled: true,
    },
    surname: {
      isPresent: true,
      disabled: false,
    },
    givenName: {
      isPresent: true,
      disabled: false,
    },
    chineseName: {
      isPresent: true,
      disabled: false,
    },
    gender: {
      isPresent: true,
      disabled: false,
    },
    dateOfBirth: {
      isPresent: true,
      disabled: false,
    },
    nationality: {
      isPresent: true,
      disabled: false,
    },
    province: {
      isPresent: true,
      disabled: false,
    },
    identityType: {
      isPresent: false,
      disabled: true,
      fullWidth: false,
    },
    isLocal: {
      isPresent: false,
      disabled: true,
    },
    needStudentVisa: {
      isPresent: false,
      disabled: true,
    },
    identityDocumentNumber: {
      isPresent: true,
      disabled: false,
    },
    PIDeclaration: {
      isPresent: true,
      disabled: false,
    },
    identityTypeCompany: {
      isPresent: false,
      disabled: true,
    },
  },
  [VIEW_MODE.VIEW_AS_APPLICANT]: {
    ...COMMON_CONFIG,
    email: {
      isPresent: true,
      disabled: true,
    },
    surname: {
      isPresent: true,
      disabled: true,
    },
    givenName: {
      isPresent: true,
      disabled: true,
    },
    chineseName: {
      isPresent: true,
      disabled: true,
    },
    gender: {
      isPresent: true,
      disabled: true,
    },
    dateOfBirth: {
      isPresent: true,
      disabled: true,
    },
    nationality: {
      isPresent: true,
      disabled: true,
    },
    province: {
      isPresent: true,
      disabled: true,
    },
    identityType: {
      isPresent: true,
      disabled: true,
      fullWidth: false,
    },
    isLocal: {
      isPresent: false,
      disabled: true,
    },
    needStudentVisa: {
      isPresent: false,
      disabled: true,
    },
    identityDocumentNumber: {
      isPresent: true,
      disabled: true,
    },
    PIDeclaration: {
      isPresent: false,
      disabled: false,
    },
    identityTypeCompany: {
      isPresent: false,
      disabled: true,
    },
  },
  [VIEW_MODE.VIEW_AS_AR_MEMBER]: {
    ...COMMON_CONFIG,
    email: {
      isPresent: true,
      disabled: true,
    },
    surname: {
      isPresent: true,
      disabled: false,
    },
    givenName: {
      isPresent: true,
      disabled: false,
    },
    chineseName: {
      isPresent: true,
      disabled: false,
    },
    gender: {
      isPresent: true,
      disabled: false,
    },
    dateOfBirth: {
      isPresent: true,
      disabled: false,
    },
    nationality: {
      isPresent: true,
      disabled: false,
    },
    province: {
      isPresent: true,
      disabled: false,
    },
    identityType: {
      isPresent: true,
      disabled: true,
      fullWidth: true,
    },
    isLocal: {
      isPresent: false,
      disabled: true,
    },
    needStudentVisa: {
      isPresent: false,
      disabled: true,
    },
    identityDocumentNumber: {
      isPresent: true,
      disabled: false,
    },
    PIDeclaration: {
      isPresent: false,
      disabled: true,
    },
    identityTypeCompany: {
      isPresent: false,
      disabled: true,
    },
  },
  [VIEW_MODE.VIEW_AS_GS_MEMBER]: {
    ...COMMON_CONFIG,
    email: {
      isPresent: true,
      disabled: true,
    },
    surname: {
      isPresent: true,
      disabled: false,
    },
    givenName: {
      isPresent: true,
      disabled: false,
    },
    chineseName: {
      isPresent: true,
      disabled: false,
    },
    gender: {
      isPresent: true,
      disabled: false,
    },
    dateOfBirth: {
      isPresent: true,
      disabled: false,
    },
    nationality: {
      isPresent: true,
      disabled: false,
    },
    province: {
      isPresent: true,
      disabled: false,
    },
    identityType: {
      isPresent: true,
      disabled: true,
      fullWidth: false,
    },
    isLocal: {
      isPresent: false,
      disabled: true,
    },
    needStudentVisa: {
      isPresent: false,
      disabled: true,
    },
    identityDocumentNumber: {
      isPresent: true,
      disabled: false,
    },
    PIDeclaration: {
      isPresent: false,
      disabled: true,
    },
    identityTypeCompany: {
      isPresent: false,
      disabled: true,
    },
  },
  [VIEW_MODE.SUPER_ADMIN]: {
    ...COMMON_CONFIG,
    email: {
      isPresent: true,
      disabled: true,
    },
    surname: {
      isPresent: true,
      disabled: false,
    },
    givenName: {
      isPresent: true,
      disabled: false,
    },
    chineseName: {
      isPresent: true,
      disabled: false,
    },
    gender: {
      isPresent: true,
      disabled: false,
    },
    dateOfBirth: {
      isPresent: true,
      disabled: false,
    },
    nationality: {
      isPresent: true,
      disabled: false,
    },
    province: {
      isPresent: true,
      disabled: false,
    },
    identityType: {
      isPresent: true,
      disabled: false, // enable edition only in super admin view
      fullWidth: true,
    },
    isLocal: {
      isPresent: true,
      disabled: false,
    },
    needStudentVisa: {
      isPresent: true,
      disabled: false,
    },
    identityDocumentNumber: {
      isPresent: true,
      disabled: false,
    },
    PIDeclaration: {
      isPresent: false,
      disabled: true,
    },
    identityTypeCompany: {
      isPresent: true,
      disabled: false,
    },
  },
  [VIEW_MODE.OTHER]: {},
};

// make VIEW_MODE.OTHER
[
  'email',
  'surname',
  'givenName',
  'chineseName',
  'gender',
  'dateOfBirth',
  'nationality',
  'province',
  'identityType',
  'isLocal',
  'needStudentVisa',
  'identityDocumentNumber',
  'addressCountryRegion',
  'addressLine1',
  'addressLine2',
  'addressLine3',
  'mobileNumber',
  'telephoneNumber',
  'disabilitySpecialType',
  'disabilitySpecialDetail',
  'PIDeclaration',
  'identityTypeCompany',
].forEach(field => {
  APPLICANT_CONFIG[VIEW_MODE.OTHER][field as keyof StepperFormContentValues] = {
    isPresent: true,
    disabled: true,
  };
});

export const getApplicantConfig = (
  viewMode: VIEW_MODE,
  formikValue: any,
): any => {
  if (Object.keys(VIEW_MODE).includes(viewMode)) {
    const CONFIG = { ...APPLICANT_CONFIG[viewMode] };

    // handle isPermanentHK
    CONFIG.nationality = {
      isPresent: CONFIG.nationality.isPresent,
      disabled:
        (formikValue.identityType === IDENTITY_TYPE.HKID_PERM &&
          viewMode !== VIEW_MODE.SUPER_ADMIN) ||
        CONFIG.nationality.disabled,
    };

    // handle isChina
    CONFIG.province = {
      isPresent: formikValue.nationality === 'CHN' && CONFIG.province.isPresent,
      disabled: CONFIG.province.disabled,
    };

    // handle disabilitySpecialTypeDetail
    CONFIG.disabilitySpecialDetail = {
      isPresent: CONFIG.disabilitySpecialDetail.isPresent,
      disabled:
        formikValue.disabilitySpecialType === 'NA' ||
        formikValue.disabilitySpecialType === undefined ||
        CONFIG.disabilitySpecialDetail.disabled,
    };

    return CONFIG;
  } else {
    return APPLICANT_CONFIG[VIEW_MODE.OTHER];
  }
};
